import { FC, Fragment, useState } from 'react';
import { Loader, Modal, ModalSaveSection } from '../../../components';
import { Autocomplete, TextField, Box, Divider } from '@mui/material';
import {
  IFieldWalkthroughDefaultMarkup,
  IFieldWalkthroughDetail,
  IFieldWalkthroughItemMarkup
} from '../../../models';
import {
  createFieldWalkthroughItemMarkups,
  deleteFieldWalkthroughItemMarkup,
  updateFieldWalkthroughItemMarkups
} from '../../../fetch';
import { useSnackbar } from 'notistack';
import { usePDFExpressWebViewer } from '../../../hooks/usePDFExpressWebViewer';
import ExpressUtils from '@pdftron/pdfjs-express-utils';
import { QueryObserverResult } from 'react-query';

interface IFloorplanModal {
  handleClose: (shouldRefetch?: boolean) => void;
  isOpen: boolean;
  fieldWalkthroughId: string;
  fieldWalkthroughItemId: number;
  defaultFloorPlans: IFieldWalkthroughDefaultMarkup[];
  isLoadingDefaultFloorPlans: boolean;
  markupFiles: IFieldWalkthroughItemMarkup[];
  refetchFieldWalkthroughDetail: () => Promise<QueryObserverResult<IFieldWalkthroughDetail, Error>>;
}

export interface IMarkupFile {
  description: string;
  fileContents?: string;
  fileDownloadName?: string;
  contentType?: string;
  storageUri?: string;
  originalFileName?: string;
  fieldWalkthroughItemFileId?: number;
  xmlAnnotationData?: string;
}

export const FloorplanModal: FC<IFloorplanModal> = ({
  handleClose,
  isOpen,
  isLoadingDefaultFloorPlans,
  fieldWalkthroughItemId,
  defaultFloorPlans,
  markupFiles,
  refetchFieldWalkthroughDetail
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<IMarkupFile[]>(markupFiles ?? []);

  const handleSaveMarkup = async (doc: IMarkupFile, index: number) => {
    try {
      const instance = viewerRefs.current[index]?.instance;
      if (instance) {
        const utils = new ExpressUtils();
        const { documentViewer, annotationManager } = instance.Core;

        const xfdf = await annotationManager.exportAnnotations({});
        const fileData = await documentViewer.getDocument().getFileData({});
        const resp = await utils.setFile(fileData).setXFDF(xfdf).merge();
        const blobOrArrayBuffer = await resp.getBlob();

        let blob: Blob;
        if (blobOrArrayBuffer instanceof ArrayBuffer) {
          blob = new Blob([blobOrArrayBuffer], { type: 'application/pdf' });
        } else {
          blob = blobOrArrayBuffer;
        }
        const formData = new FormData();
        formData.append('description', doc.description);
        formData.append('Image', blob, `${doc.description}.pdf`);
        formData.append('XmlAnnotationData', xfdf);
        if (doc.fieldWalkthroughItemFileId) {
          await updateFieldWalkthroughItemMarkups(doc.fieldWalkthroughItemFileId, formData);
        } else {
          await createFieldWalkthroughItemMarkups(fieldWalkthroughItemId, formData);
        }
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar(error?.Detail ?? `Error saving floor plans, please try again.`, {
        variant: 'error'
      });
    }
  };

  const handleDeleteMarkup = async (doc: IMarkupFile, index: number) => {
    const result = window.confirm('Are you sure you want to delete this floor plan?');
    if (result) {
      try {
        setIsLoading(true);
        await deleteFieldWalkthroughItemMarkup(doc.fieldWalkthroughItemFileId);
        await refetchFieldWalkthroughDetail();
        enqueueSnackbar(`Floor plan deleted!`, { variant: 'success' });
      } catch (error) {
        console.log(error);
        enqueueSnackbar(error?.Detail ?? `Error deleteing floor plan, please try again.`, {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };
  const { viewerRefs } = usePDFExpressWebViewer({
    files: selectedOptions,
    handleDocDelete: handleDeleteMarkup,
    isReadOnly: false
  });
  return (
    <Modal title="Floorplan Markup" open={isOpen} maxWidth="lg" onClose={() => handleClose()}>
      <Box mt={1}>
        {(isLoadingDefaultFloorPlans || isLoading) && <Loader type="overlay" position="centered" />}
        <Box mb={1}>
          <Autocomplete
            size="small"
            multiple
            disabled={isLoadingDefaultFloorPlans}
            options={
              defaultFloorPlans
                ? ([...markupFiles, ...defaultFloorPlans]?.map(plan => plan) as IMarkupFile[])
                : []
            }
            filterSelectedOptions
            getOptionLabel={option => {
              if (!!option.fieldWalkthroughItemFileId) {
                return `${option.description} (Edited)`;
              }
              return `${option.description}`;
            }}
            value={selectedOptions}
            onChange={(event, newValue: IFieldWalkthroughDefaultMarkup[]) => {
              if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
                setSelectedOptions([]);
              } else if (Array.isArray(newValue)) {
                setSelectedOptions(newValue.map(option => option));
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label={isLoadingDefaultFloorPlans ? 'Loading...' : 'Floor Plans'}
              />
            )}
          />
        </Box>
        {selectedOptions.length > 0 && (
          <Fragment>
            {selectedOptions.map((option, index) => {
              return (
                <Fragment key={`${index}`}>
                  <Box height="73vh" mt={1} ref={el => (viewerRefs.current[index] = el)} />
                  <Divider />
                </Fragment>
              );
            })}
          </Fragment>
        )}
      </Box>
      <ModalSaveSection
        handleCancel={() => handleClose()}
        saveButtonText="Save"
        isSaveDisabled={isLoading}
        handleSave={async () => {
          setIsLoading(true);

          if (selectedOptions.length === 0) {
            enqueueSnackbar('No floor plans to save.', { variant: 'info' });
            setIsLoading(false);
            return;
          }

          await Promise.all(
            selectedOptions.map(async (doc, index) => {
              await handleSaveMarkup(doc, index);
            })
          );
          enqueueSnackbar(`Floor plan saved!`, { variant: 'success' });

          await refetchFieldWalkthroughDetail();
          setIsLoading(false);
        }}
      />
    </Modal>
  );
};
